import React, { useState, useContext, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import AuthContext from "contexts/AuthContext";
import { Input } from "components/input";
import CloseIcon from "assets/icons/close.svg";
import { Button } from "components/button";
import { Address } from "components/address";
import { isValidEmail, useCurrentLocation } from "lib/utils";
import { useTranslation } from "react-i18next";
import SingleCheckbox from "components/checkbox";
import { isLegoEmail } from "consts/lego-admin";
import { useUpdateUser } from "api/use-user";
import { useGetCustomer } from "api/tes/useGetCustomer";
import { useUpdateCustomer } from "api/tes/useUpdateCustomer";
import LoadingSpinner from "components/loading-spinner";

export default function AccountModal({ show, onClose, customerId }) {
  const { user } = useContext(AuthContext);

  // Fetch customer data if customerId is provided
  const { data: customerData, isLoading: isLoadingCustomer } = useGetCustomer(
    customerId,
    { enabled: Boolean(customerId) }
  );

  // Use customer data if available, otherwise use authenticated user
  const currentUser = customerId ? customerData?.data?.customer : user;
  const [activeTab, setActiveTab] = useState("personal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [personalDetailsError, setPersonalDetailsError] = useState(null);
  const [emailPreferencesError, setEmailPreferencesError] = useState(null);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [address, setAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressCountry: "US",
    addressZip: "",
    addressState: "",
  });

  // Initialize form data when currentUser changes
  useEffect(() => {
    if (currentUser) {
      setEmail(currentUser.email || "");
      setFirstName(currentUser.first_name || currentUser.name || "");
      setLastName(currentUser.last_name || "");
      setAddress({
        addressLine1: currentUser.street || currentUser.address_line1 || "",
        addressLine2: currentUser.address_line2 || "",
        addressCity: currentUser.city || "",
        addressCountry: currentUser.country || "US",
        addressZip: currentUser.zip || currentUser.postal_code || "",
        addressState: currentUser.state || "",
      });
    }
  }, [currentUser]);

  const { location: currentLocation } = useCurrentLocation();
  const modalRef = useRef(null);
  const { t } = useTranslation();
  // Email preferences state
  const [isSubscribedToSurvey, setIsSubscribedToSurvey] = useState(false);
  const [isSubscribedToMailReminders, setIsSubscribedToMailReminders] =
    useState(false);
  const [isNotInterested, setIsNotInterested] = useState(false);

  // Initialize email preferences when currentUser changes
  useEffect(() => {
    if (currentUser) {
      // For customers, preferences are in the data field with different keys
      const surveyPref = customerId
        ? currentUser.data?.isSubscribedToSurvey
        : currentUser.u_lego_enable_survey_emails;
      const marketingPref = customerId
        ? currentUser.data?.isSubscribedToReminders
        : currentUser.u_lego_enable_marketing_emails;

      setIsSubscribedToSurvey(surveyPref === true);
      setIsSubscribedToMailReminders(marketingPref === true);
      setIsNotInterested(!surveyPref && !marketingPref);
    }
  }, [currentUser, customerId]);

  // Construct the existing address string
  const existingAddress =
    currentUser?.street || currentUser?.address_line1
      ? `${currentUser.street || currentUser.address_line1}, ${currentUser.city}, ${currentUser.state} ${currentUser.zip || currentUser.postal_code}`
      : "";

  // Mutually exclusive logic
  useEffect(() => {
    if (isNotInterested) {
      setIsSubscribedToSurvey(false);
      setIsSubscribedToMailReminders(false);
    }
  }, [isNotInterested]);
  useEffect(() => {
    if (isSubscribedToSurvey || isSubscribedToMailReminders) {
      setIsNotInterested(false);
    }
  }, [isSubscribedToSurvey, isSubscribedToMailReminders]);

  // Scroll to top when submitting
  useEffect(() => {
    if (isSubmitting && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [isSubmitting]);

  useEffect(() => {
    const validEmail = isValidEmail(email);
    const isLego = isLegoEmail(email);
    setEmailError(!validEmail || isLego);
  }, [email]);

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSuccess(false);
        onClose();
      }, 2000);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const { mutate: updateCustomer } = useUpdateCustomer({
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSuccess(false);
        onClose();
      }, 2000);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = () => {
    if (personalDetailsError || emailPreferencesError || noChanges) return;

    setIsSubmitting(true);

    if (customerId) {
      // Transform customer update to user API format
      const userPayload = {
        userEmail: currentUser.email, // Use current email for identifying the user
        first_name: firstName,
        last_name: lastName,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        addressCity: address.addressCity,
        addressState: address.addressState,
        addressZip: address.addressZip,
        addressCountry: address.addressCountry,
        isSubscribedToSurvey: isSubscribedToSurvey,
        isSubscribedToReminders: isSubscribedToMailReminders,
      };

      // Handle email change for customer
      if (email !== currentUser.email) {
        userPayload.newEmail = email;
      }

      updateCustomer({
        userPayload: userPayload,
      });
    } else {
      // Update authenticated user
      const payload = {
        userEmail: user.email,
        first_name: firstName,
        last_name: lastName,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        addressCity: address.addressCity,
        addressCountry: address.addressCountry,
        addressZip: address.addressZip,
        addressState: address.addressState,
        isSubscribedToSurvey: isSubscribedToSurvey,
        isSubscribedToReminders: isSubscribedToMailReminders,
      };

      if (email !== user.email) {
        payload.newEmail = email;
        payload.redirectTo = `${window.location.origin}/en-US/trade-in/sign-in?signUpOnly=true&email=${encodeURIComponent(email)}`;
        localStorage.setItem(`emailInput`, email);
      }

      updateUser(payload);
    }
  };

  // set personal details error
  useEffect(() => {
    const hasEmptyRequiredFields =
      !email ||
      emailError ||
      !firstName ||
      !lastName ||
      !address.addressLine1 ||
      !address.addressCity ||
      !address.addressCountry ||
      !address.addressZip ||
      !address.addressState;

    setPersonalDetailsError(hasEmptyRequiredFields);
  }, [email, emailError, firstName, lastName, address]);

  // set email preferences error (only for non-customer users)
  useEffect(() => {
    if (customerId) {
      setEmailPreferencesError(false);
      return;
    }
    const hasEmptyRequiredFields =
      !isSubscribedToSurvey && !isSubscribedToMailReminders && !isNotInterested;
    setEmailPreferencesError(hasEmptyRequiredFields);
  }, [
    isSubscribedToSurvey,
    isSubscribedToMailReminders,
    isNotInterested,
    customerId,
  ]);

  // detect if no changes have been made
  const noChanges =
    email === currentUser?.email &&
    firstName === (currentUser?.first_name || currentUser?.name) &&
    lastName === currentUser?.last_name &&
    address.addressLine1 ===
      (currentUser?.street || currentUser?.address_line1) &&
    address.addressCity === currentUser?.city &&
    address.addressZip === (currentUser?.zip || currentUser?.postal_code) &&
    address.addressState === currentUser?.state &&
    address.addressCountry === (currentUser?.country || "US") &&
    (customerId
      ? isSubscribedToSurvey ===
          (currentUser?.data?.isSubscribedToSurvey === true) &&
        isSubscribedToMailReminders ===
          (currentUser?.data?.isSubscribedToReminders === true)
      : isSubscribedToSurvey ===
          (currentUser?.u_lego_enable_survey_emails === true) &&
        isSubscribedToMailReminders ===
          (currentUser?.u_lego_enable_marketing_emails === true));

  if (!show) return null;

  // Show loading if fetching customer data
  if (customerId && isLoadingCustomer) {
    return (
      <div className="fixed w-screen h-screen top-0 left-0 z-50">
        <div className="bg-[#000000b6] w-full h-full fixed" onClick={onClose} />
        <div className="bg-white rounded-xl mt-[5vh] w-[min(700px,90vw)] mx-auto p-6 relative shadow-xl h-fit max-h-[90vh] flex items-center justify-center">
          <LoadingSpinner isLoading="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed w-screen h-screen top-0 left-0 z-50">
      <motion.div
        className="bg-[#000000b6] w-full h-full fixed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className={`bg-white rounded-xl scrollbar-hide mt-[5vh] w-[min(700px,90vw)] mx-auto p-6 relative shadow-xl h-fit max-h-[90vh] ${isSubmitting ? "overflow-y-hidden" : "overflow-y-auto"}`}
        style={{ pointerEvents: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {isSubmitting && (
          <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full flex items-center justify-center bg-white z-10">
            <LoadingSpinner
              isLoading={"Saving..."}
              isSuccess={isSuccess ? "Success!" : null}
            />
          </div>
        )}
        <div
          className="absolute top-6 right-6 cursor-pointer"
          onClick={onClose}
        >
          <img src={CloseIcon} alt="Close" />
        </div>
        <h2 className="text-3xl font-bold mb-6">
          {customerId ? "Edit Seller Details" : "Manage account"}
        </h2>
        <div className="flex border-b border-gray-300 mb-6">
          <button
            className={`pr-4 py-2 ${activeTab === "personal" ? "text-blue" : personalDetailsError ? "text-black-300 flex items-center before:content-['!'] before:mr-1" : "text-black-300"}`}
            onClick={() => setActiveTab("personal")}
          >
            Personal details
          </button>
          <button
            className={`px-4 py-2 ${activeTab === "email" ? "text-blue" : emailPreferencesError ? "text-black-300 flex items-center before:content-['!'] before:mr-1" : "text-black-300"}`}
            onClick={() => setActiveTab("email")}
          >
            Email communication preferences
          </button>
        </div>
        <div className={`${activeTab === "personal" ? "block" : "hidden"}`}>
          <form className="flex flex-col space-y-5">
            <>
              <Input
                label="Email address"
                type="email"
                name="email"
                warningOnlyAfterBlur
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                showWarning={emailError || customerId}
                warningMessage={
                  emailError
                    ? isLegoEmail(email)
                      ? "Sorry! LEGO.com email addresses cannot be used to update an account."
                      : "Please enter a valid email address"
                    : null
                }
              />
            </>
            <div className="flex flex-col md:flex-row space-y-5 md:space-y-0 md:space-x-x-large">
              <div className="flex-1">
                <Input
                  type="text"
                  label="First name"
                  name="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  showWarning={!firstName}
                  warningMessage="This field is required"
                  warningOnlyAfterBlur
                />
              </div>
              <div className="flex-1">
                <Input
                  type="text"
                  label="Last name"
                  name="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  showWarning={!lastName}
                  warningMessage="This field is required"
                  warningOnlyAfterBlur
                />
              </div>
            </div>
            <div>
              <Address
                address={address}
                setAddress={setAddress}
                currentLocation={currentLocation}
                existingAddress={existingAddress}
              />
            </div>
          </form>
        </div>
        <div
          className={`${activeTab === "email" ? "block" : "hidden"} text-black-400`}
        >
          <div className="w-full flex flex-col space-y-4">
            <p className="text-base text-secondary mb-2 w-full">
              {t("sign-up-email-preferences.subtitle")}
            </p>
            <div className="w-full flex flex-col space-y-6">
              <SingleCheckbox
                label={t("sign-up-email-preferences.marketingOption")}
                checked={isSubscribedToSurvey}
                setChecked={(checked) => {
                  if (
                    !checked &&
                    !isSubscribedToMailReminders &&
                    !isNotInterested
                  )
                    return;
                  setIsSubscribedToSurvey(checked);
                  if (checked) setIsNotInterested(false);
                }}
                disabled={isNotInterested}
              />
              <SingleCheckbox
                label={t("sign-up-email-preferences.updatesOption")}
                checked={isSubscribedToMailReminders}
                setChecked={(checked) => {
                  if (!checked && !isSubscribedToSurvey && !isNotInterested)
                    return;
                  setIsSubscribedToMailReminders(checked);
                  if (checked) setIsNotInterested(false);
                }}
                disabled={isNotInterested}
              />
              <SingleCheckbox
                label={t("sign-up-email-preferences.selectAll")}
                checked={isNotInterested}
                setChecked={(checked) => {
                  setIsNotInterested(checked);
                  if (checked) {
                    setIsSubscribedToSurvey(false);
                    setIsSubscribedToMailReminders(false);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-8 gap-4 w-full">
          <Button
            type="button"
            variant="secondary"
            className="px-6 py-2 text-base"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="px-6 py-2 text-base font-semibold"
            disabled={
              personalDetailsError ||
              emailPreferencesError ||
              noChanges ||
              isSubmitting
            }
            onClick={onSubmit}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
